import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { addToast } from "../../../Store/Actions";
import axiosInstance from "../../../axiosInstance";
import URL from "../../../urls";
import { useAiChatCentrifugo } from "./useAiChatCentrifugo.js";
import FlexBox from "../FlexBox";
import AiChatHeader from "./AiChatHeader";
import AiChatInput from "./AiChatInput";
import AiChatIntroSection from "./AiChatIntroSection";
import AiChatMessageList from "./AiChatMessageList";
import AiChatSuggestivePrompts from "./AiChatSuggestivePrompts";
import ReportReasonModal from "./ReportReasonModal";
import CustomisationCard from "./AiCustomizationEntry";
import { createMessage, mergeFinalContent } from "./aiChatHelpers";
import Bugsnag from "@bugsnag/js";
import { trackEvent } from "../../../helperFunctions";
import { NumberParam, useQueryParam } from "use-query-params";
import {
  setMessages,
  setLiveContext,
  clearChatSession,
  setMessageSent,
  updateMessageById,
  setCallout,
  setLastChatConversationId,
} from "../../../Store/Actions/aiChatActions";
import useMobileView from "../../../hooks/useMobileView";
import { Body2 } from "../Headings";
import { DAVYS_GREY } from "../colors";
const Container = styled(FlexBox)`
  height: 100%;
  width: 100%;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
`;

const ChatBody = styled(FlexBox)`
  flex-direction: column;
  flex-grow: 1;
  padding: 1rem;
  overflow-y: auto;

  & > * {
    margin-top: auto;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const ChatBodyContent = styled(FlexBox)`
  width: 100%;
  flex-direction: column;
  row-gap: 1rem;
`;

const ReportSheetOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 50;
`;

const ReportSheetRoot = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const greetings = [
  "How can I assist you?",
  "Ready when you are!",
  "Want me to sort something out quickly?",
  "Let's ease into the day—how can I help?",
  "Anything I can take off your plate today?",
  "Tell me what would feel like a win today.",
];

const AI_CHAT_GREETING_SESSION_KEY = "aiChatGreeting";
const AI_CHAT_TOOLTIP_STEP_KEY = "aiChatTooltipStep";
const TOOLTIP_IDLE_DELAY_MS = 5000;
const TOOLTIP_AUTO_DISMISS_MS = 5000;
const TOOLTIP_SEQUENCE = ["new-chat", "help"];
export const AI_CHAT_PROMPTS_SESSION_KEY = "aiChatPrompts";
const CENTRIFUGO_ERROR_TOAST_COOLDOWN_MS = 10000;

const NewChatView = ({
  onHistoryClick,
  onClose,
  toggleHelp,
  prefilledPrompt,
  onPromptPrefilled,
  activeConversation,
  onNewChatStarted,
  isChatOpen,
  onLoadMoreMessages,
  hasMoreMessages,
  isLoadingMoreMessages,
  openCustomisationSettings,
  selectedClientId,
  setSelectedClientId,
  textareaRef,
  lastConversationFetchedId,
  setLastConversationFetchedId,
  thinking,
  setThinking,
}) => {
  const dispatch = useDispatch();
  const isMobile = useMobileView();
  const [clientId] = useQueryParam("client_id", NumberParam);

  const user = useSelector(state => state.auth.user);
  const twaAIEvents = useSelector(state => state.twaAI?.events || []);
  const callout = useSelector(state => state.aiChat.callout);
  const messages = useSelector(state => state.aiChat.messages);
  const liveContext = useSelector(state => state.aiChat.liveContext);
  const messageSent = useSelector(state => state.aiChat.messageSent);
  const lastChatConversationId = useSelector(
    state => state.aiChat.lastChatConversationId
  );
  const lastCentrifugoErrorToastRef = useRef(0);
  const messageIdsRef = useRef(new Set());
  const currentStreamingMessageIdRef = useRef(null);
  const chatBodyRef = useRef(null);
  const topLoaderRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const reportAbortControllerRef = useRef(null);
  const feedbackAbortControllerRef = useRef(null);
  const assistantAbortControllerRef = useRef(null);

  const [centrifugoChannel, setCentrifugoChannel] = useState(null);
  const [reportSheetContext, setReportSheetContext] = useState(null);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  const [hasPromptBeenSent, setHasPromptBeenSent] = useState(true);
  const [typingActivityKey, setTypingActivityKey] = useState(0);

  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipStep, setTooltipStep] = useState(() => {
    const saved = sessionStorage.getItem(AI_CHAT_TOOLTIP_STEP_KEY);
    return saved !== null ? Number(saved) : 0;
  });
  const [hasUserExplicitlyDismissed, setHasUserExplicitlyDismissed] =
    useState(false);

  const [greeting] = useState(() => {
    const savedGreeting = sessionStorage.getItem(AI_CHAT_GREETING_SESSION_KEY);
    if (savedGreeting) return savedGreeting;
    return greetings[Math.floor(Math.random() * greetings.length)];
  });

  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [disableChat, setDisableChat] = useState(!!lastChatConversationId);
  const [showCustomizationEntry, setShowCustomizationEntry] = useState(false);
  const [completedTyping, setCompletedTyping] = useState({});
  const [displayed, setDisplayed] = useState("");
  const displayedLengthRef = useRef(0);
  const intervalRef = useRef(null);
  const hasMessages = messages.length > 0;

  const shouldShowCustomizationNudge = twaAIEvents.some(
    event => event.event_type === "customization_nudge"
  );
  const userMessage = JSON.parse(
    sessionStorage.getItem("clientmessage") || "{}"
  );

  useEffect(() => {
    if (shouldShowCustomizationNudge) {
      setShowCustomizationEntry(true);
    }
  }, [shouldShowCustomizationNudge]);

  useEffect(() => {
    if (
      userMessage?.message &&
      userMessage?.clientId &&
      userMessage?.clientName
    ) {
      handleSendMessage(
        `Summarise the journey for client ${userMessage?.clientName}, CID ${userMessage?.clientId}`
      );
      sessionStorage.removeItem("clientmessage");
    }
  }, [userMessage]);

  useEffect(() => {
    sessionStorage.setItem(AI_CHAT_GREETING_SESSION_KEY, greeting);
  }, [greeting]);

  useEffect(() => {
    sessionStorage.setItem(AI_CHAT_TOOLTIP_STEP_KEY, String(tooltipStep));
  }, [tooltipStep]);

  useEffect(() => {
    if (
      !hasPromptBeenSent ||
      !hasMessages ||
      activeTooltip ||
      tooltipStep >= TOOLTIP_SEQUENCE.length ||
      hasUserExplicitlyDismissed
    ) {
      return;
    }

    const idleTimer = setTimeout(() => {
      const tooltip = TOOLTIP_SEQUENCE[tooltipStep];
      setActiveTooltip(tooltip);
      setTooltipStep(prev => prev + 1);
    }, TOOLTIP_IDLE_DELAY_MS);

    return () => clearTimeout(idleTimer);
  }, [
    hasPromptBeenSent,
    hasMessages,
    typingActivityKey,
    activeTooltip,
    tooltipStep,
    hasUserExplicitlyDismissed,
  ]);

  useEffect(() => {
    if (!activeTooltip) return;

    const dismissTimer = setTimeout(() => {
      setActiveTooltip(null);
    }, TOOLTIP_AUTO_DISMISS_MS);

    return () => clearTimeout(dismissTimer);
  }, [activeTooltip]);

  useEffect(() => {
    const fetchSuggestedPrompts = async () => {
      try {
        const savedPrompts = sessionStorage.getItem(
          AI_CHAT_PROMPTS_SESSION_KEY
        );

        if (savedPrompts) {
          setSuggestedPrompts(JSON.parse(savedPrompts));
          return;
        }

        const response = await axiosInstance.get(URL.suggestedPrompts);
        const prompts = response?.data?.data?.prompts || [];

        setSuggestedPrompts(prompts);

        sessionStorage.setItem(
          AI_CHAT_PROMPTS_SESSION_KEY,
          JSON.stringify(prompts)
        );
      } catch (error) {
        Bugsnag.notify(error);
      }
    };

    if (isChatOpen) {
      fetchSuggestedPrompts();
    }
  }, [isChatOpen]);

  const loadOlderMessages = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreMessages || !onLoadMoreMessages) {
      return;
    }

    loadingMoreRef.current = true;
    const container = chatBodyRef.current;

    if (!container) {
      loadingMoreRef.current = false;
      return;
    }

    const previousHeight = container.scrollHeight;
    const olderMessages = await onLoadMoreMessages();

    if (olderMessages?.length) {
      dispatch(setMessages(prev => [...olderMessages, ...prev]));

      requestAnimationFrame(() => {
        const newHeight = container.scrollHeight;
        container.scrollTop = newHeight - previousHeight + container.scrollTop;
      });
    }

    loadingMoreRef.current = false;
  }, [hasMoreMessages, onLoadMoreMessages, dispatch]);

  useEffect(() => {
    const container = chatBodyRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (
        container.scrollTop <= 50 &&
        hasMoreMessages &&
        !isLoadingMoreMessages
      ) {
        loadOlderMessages();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [loadOlderMessages, hasMoreMessages, isLoadingMoreMessages, messages]);

  const handleTooltipDismiss = reason => {
    setActiveTooltip(null);
    if (reason === "interacted") {
      setHasUserExplicitlyDismissed(true);
    }
  };

  const handleTypingActivity = () => {
    setTypingActivityKey(prev => prev + 1);
    if (activeTooltip) {
      setActiveTooltip(null);
    }
  };

  const handlePromptSent = () => {
    setHasPromptBeenSent(true);
  };

  const handleUpdateMessage = (messageId, updater) => {
    dispatch(updateMessageById(messageId, updater));
  };

  const handleCentrifugoMessage = useCallback(
    data => {
      if (!data) return;

      if (data.type === "twa_ai.chat.chunk") {
        setThinking(false);

        const {
          chunk,
          done,
          chat_id,
          content,
          thread_id,
          feedback,
          conversation_id,
        } = data;
        const streamingId = currentStreamingMessageIdRef.current;

        if (!done && chunk) {
          if (!streamingId) {
            const tempId = `ai-stream-${Date.now()}`;
            currentStreamingMessageIdRef.current = tempId;

            const createStreamingMessage = createMessage(
              "ai",
              typeof chunk === "string" ? chunk : "",
              {
                id: tempId,
                isStreaming: true,
                timestamp: Date.now(),
                toolEvents: typeof chunk === "object" ? [chunk] : [],
              }
            );

            dispatch(setMessages(prev => [...prev, createStreamingMessage]));
          } else {
            handleUpdateMessage(streamingId, msg => ({
              markdownText:
                typeof chunk === "string"
                  ? (msg.markdownText || "") + chunk
                  : msg.markdownText,
              toolEvents:
                typeof chunk === "object"
                  ? [...(msg.toolEvents || []), chunk]
                  : msg.toolEvents,
            }));
          }
        }

        if (done && streamingId) {
          handleUpdateMessage(streamingId, msg => {
            const failedToolEvent = (msg.toolEvents || []).find(
              event => event?.status === "failed"
            );

            const currentText = msg.markdownText || "";
            const finalText = content || chunk || "";
            setSelectedClientId(data?.client_id);
            return {
              id: chat_id || msg.id,
              markdownText: mergeFinalContent(currentText, finalText),
              conversationThreadId: thread_id,
              timestamp: Date.now(),
              feedback,
              isStreaming: true,
              clientName: data?.client_name || null,
              clientCid: data?.client_id || null,
              toolEvents: msg.toolEvents || [],
              conversationId: conversation_id || null,
              errorInfo: failedToolEvent
                ? {
                    errorTitle: "We couldn’t generate a response.",
                    errorDescription: `A response couldn’t be generated due to ${
                      failedToolEvent.summary || failedToolEvent.error
                    } reason.`,
                    errorSuggestion:
                      "Try rephrasing your prompt. If the issue persists, reach out to support.",
                  }
                : null,
            };
          });

          currentStreamingMessageIdRef.current = null;
          setCentrifugoChannel(null);
          setDisableChat(false);
        }

        return;
      }

      if (!data.content && !data.chunk) return;

      const messageId =
        data.id || `ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      if (messageIdsRef.current.has(messageId)) {
        return;
      }

      messageIdsRef.current.add(messageId);

      const aiMessage = createMessage("ai", data.content || data.chunk || "", {
        id: messageId,
        conversationThreadId: data.thread_id || undefined,
        timestamp: data.timestamp || Date.now(),
      });

      dispatch(
        setMessages(prev => {
          if (prev.some(m => m.id === messageId)) {
            return prev;
          }
          return [...prev, aiMessage];
        })
      );
    },
    [centrifugoChannel, dispatch]
  );

  const handleCentrifugoError = useCallback(
    error => {
      if (!centrifugoChannel) return;

      const now = Date.now();
      if (
        now - lastCentrifugoErrorToastRef.current <
        CENTRIFUGO_ERROR_TOAST_COOLDOWN_MS
      ) {
        return;
      }

      lastCentrifugoErrorToastRef.current = now;

      dispatch(
        addToast({
          msg: "Connection issue: Message sync may be delayed",
          appearance: "warning",
          showBottomToastOnMobile: true,
        })
      );
      setThinking(false);
      setDisableChat(false);
      Bugsnag.notify(error);
    },
    [centrifugoChannel, dispatch]
  );

  useAiChatCentrifugo({
    channel: centrifugoChannel,
    enabled: isChatOpen,
    onMessage: handleCentrifugoMessage,
    onError: handleCentrifugoError,
  });

  const handleSendMessage = async promptText => {
    const trimmedPrompt = promptText?.trim();
    if (!trimmedPrompt) return;
    dispatch(setMessageSent(true));
    if (!shouldShowCustomizationNudge) {
      setShowCustomizationEntry(false);
    }
    if (
      (!messageSent || lastConversationFetchedId) &&
      activeConversation?.messages?.[activeConversation.messages.length - 1]
        ?.clientCid
    ) {
      dispatch(setCallout("generic"));
    } else {
      dispatch(setCallout(null));
    }
    setLastConversationFetchedId(null);

    setThinking(true);
    setDisableChat(true);
    setDisplayed("");
    displayedLengthRef.current = 0;
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    const clientIdToTrack =
      selectedClientId ||
      activeConversation?.messages?.[activeConversation.messages.length - 1]
        ?.clientCid;
    const customerId = clientIdToTrack || user?.id;

    const latestMessage = messages[messages.length - 1];
    const contextConversationId =
      liveContext.conversationId || activeConversation?.id;
    const contextThreadId =
      liveContext.externalConversationThreadId ||
      latestMessage?.externalConversationThreadId ||
      latestMessage?.conversationThreadId;
    const contextLastMessageId = liveContext.lastMessageId || latestMessage?.id;

    const userMessage = createMessage("user", trimmedPrompt);

    dispatch(setMessages(prev => [...prev, userMessage]));
    setHasPromptBeenSent(true);

    if (!customerId) {
      dispatch(
        addToast({
          msg: "Unable to send message right now.",
          appearance: "error",
          showBottomToastOnMobile: true,
        })
      );
      return;
    }

    trackEvent({
      event: "twa_ai_assistant_questioned",
      payload: {
        client_id: clientIdToTrack ? clientIdToTrack : null,
        source: clientId ? "client" : "dashboard",
      },
    });
    const assistantUrl = URL.newAssistant;

    const payload = {
      message: trimmedPrompt,
      ...(contextConversationId
        ? { conversation_id: contextConversationId }
        : {}),
      ...(contextThreadId ? { thread_id: contextThreadId } : {}),
      ...(contextLastMessageId
        ? { last_message_id: contextLastMessageId }
        : {}),
      api_version: "v3",
    };

    assistantAbortControllerRef.current?.abort();

    const controller = new AbortController();
    assistantAbortControllerRef.current = controller;

    try {
      const response = await axiosInstance.post(assistantUrl, payload, {
        signal: controller.signal,
      });
      const responseData = response?.data || {};

      const nextConversationId =
        responseData?.conversation_id || contextConversationId;
      dispatch(setLastChatConversationId(nextConversationId || null));
      const nextThreadId = responseData?.thread_id || contextThreadId;
      const nextLastMessageId =
        responseData?.last_message_id || responseData?.chat_id || "";
      const channel = responseData?.channel;

      if (nextConversationId || nextThreadId || nextLastMessageId) {
        dispatch(
          setLiveContext({
            conversationId: nextConversationId || "",
            threadId: nextThreadId || "",
            lastMessageId: nextLastMessageId || "",
          })
        );
      }

      if (channel) {
        currentStreamingMessageIdRef.current = null;
        setCentrifugoChannel(channel);
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
      dispatch(
        addToast({
          msg:
            error?.response?.data?.status?.message || "Failed to send message",
          appearance: "error",
          showBottomToastOnMobile: true,
        })
      );
      setThinking(false);
      setDisableChat(false);
      Bugsnag.notify(error);
    }
  };

  const handleOpenReportSheet = context => {
    setReportSheetContext(context);
  };

  const handleCloseReportSheet = () => {
    setReportSheetContext(null);
  };

  const handleSubmitReportSheet = async ({ reasons, other_text }) => {
    const messageId = reportSheetContext?.messageId;
    const conversationId =
      reportSheetContext?.message?.conversationId ||
      liveContext?.conversationId ||
      activeConversation?.id;

    reportAbortControllerRef.current?.abort();

    const controller = new AbortController();
    reportAbortControllerRef.current = controller;

    setIsSubmittingReport(true);

    try {
      const reportsUrl = URL.aiChatReports.replace(
        ":conversation_id",
        conversationId
      );

      await axiosInstance.post(
        reportsUrl,
        {
          chat_id: messageId,
          reasons,
          ...(other_text ? { other_text } : {}),
        },
        {
          signal: controller.signal,
        }
      );

      if (messageId) {
        handleUpdateMessage(messageId, () => ({ isReported: true }));
      }

      dispatch(
        addToast({
          msg: "Your report has been sent.",
          appearance: "info",
          showBottomToastOnMobile: true,
        })
      );

      setReportSheetContext(null);
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }

      dispatch(
        addToast({
          msg: "Failed to submit report",
          appearance: "error",
          showBottomToastOnMobile: true,
        })
      );
    } finally {
      if (reportAbortControllerRef.current === controller) {
        reportAbortControllerRef.current = null;
      }
      setIsSubmittingReport(false);
    }
  };

  const handleNewChat = () => {
    dispatch(setLastChatConversationId(null));
    dispatch(clearChatSession());
    setCentrifugoChannel(null);
    messageIdsRef.current.clear();
    currentStreamingMessageIdRef.current = null;
    sessionStorage.removeItem(AI_CHAT_GREETING_SESSION_KEY);
    onNewChatStarted?.();
    dispatch(
      setLiveContext({ conversationId: "", threadId: "", lastMessageId: "" })
    );
    textareaRef.current?.focus();
  };

  const handleRegenerate = async (message, messageId) => {
    const regenerateText = (
      message?.markdownText ||
      message?.message ||
      ""
    ).trim();

    if (!regenerateText) return;

    setThinking(true);
    setDisableChat(true);
    dispatch(setMessageSent(true));
    setDisplayed("");
    displayedLengthRef.current = 0;
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    handleUpdateMessage(messageId, () => ({
      markdownText: "",
      isReported: false,
      isStreaming: true,
      toolEvents: [],
    }));

    const customerId =
      activeConversation?.clientId ||
      activeConversation?.customerId ||
      user?.id;

    const latestMessage = messages[messages.length - 1];

    const contextConversationId =
      liveContext.conversationId || activeConversation?.id;

    const contextThreadId =
      message?.externalConversationThreadId ||
      latestMessage?.externalConversationThreadId ||
      message?.conversationThreadId ||
      liveContext.threadId ||
      latestMessage?.conversationThreadId;

    const contextLastMessageId =
      messageId || liveContext.lastMessageId || latestMessage?.id;

    if (!customerId) {
      dispatch(
        addToast({
          msg: "Unable to regenerate message right now.",
          appearance: "error",
          showBottomToastOnMobile: true,
        })
      );
      return;
    }

    const assistantUrl = URL.newAssistant;

    const payload = {
      message: regenerateText,
      ...(contextConversationId
        ? { conversation_id: contextConversationId }
        : {}),
      ...(contextThreadId ? { thread_id: contextThreadId } : {}),
      ...(contextLastMessageId
        ? { last_message_id: contextLastMessageId }
        : {}),
      api_version: "v3",
      is_regeneration: true,
    };

    assistantAbortControllerRef.current?.abort();

    const controller = new AbortController();
    assistantAbortControllerRef.current = controller;

    try {
      const response = await axiosInstance.post(assistantUrl, payload, {
        signal: controller.signal,
      });
      const responseData = response?.data || {};

      const nextConversationId =
        responseData?.conversation_id || contextConversationId;
      const nextThreadId = responseData?.thread_id || contextThreadId;
      const nextLastMessageId =
        responseData?.last_message_id || responseData?.chat_id || "";
      const channel = responseData?.channel;

      if (nextConversationId || nextThreadId || nextLastMessageId) {
        dispatch(
          setLiveContext({
            conversationId: nextConversationId || "",
            threadId: nextThreadId || "",
            lastMessageId: nextLastMessageId || "",
          })
        );
      }

      if (channel) {
        currentStreamingMessageIdRef.current = messageId;
        setCentrifugoChannel(channel);
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
      Bugsnag.notify(error);
      dispatch(
        addToast({
          msg:
            error?.response?.data?.status?.message ||
            "Failed to regenerate message",
          appearance: "error",
          showBottomToastOnMobile: true,
        })
      );

      setThinking(false);
      setDisableChat(false);
    }
  };

  const submitFeedback = async (messageId, vote, previousFeedback) => {
    const conversationId = activeConversation?.id;
    if (!(conversationId || lastChatConversationId) || !messageId) return;

    feedbackAbortControllerRef.current?.abort();

    const controller = new AbortController();
    feedbackAbortControllerRef.current = controller;

    const feedbackUrl = URL.aiChatFeedback
      .replace(":conversation_id", conversationId || lastChatConversationId)
      .replace(":chat_id", messageId);

    try {
      await axiosInstance.patch(
        feedbackUrl,
        { vote: vote },
        {
          signal: controller.signal,
        }
      );

      dispatch(
        addToast({
          msg: "Thank you for your feedback.",
          appearance: "info",
          showBottomToastOnMobile: true,
        })
      );
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }

      handleUpdateMessage(messageId, () => ({ feedback: previousFeedback }));

      dispatch(
        addToast({
          msg: "Failed to save feedback. Please try again.",
          appearance: "error",
          showBottomToastOnMobile: true,
        })
      );
    } finally {
      if (feedbackAbortControllerRef.current === controller) {
        feedbackAbortControllerRef.current = null;
      }
    }
  };

  const handleLike = (message, messageId) => {
    const previousFeedback = message?.feedback;
    handleUpdateMessage(messageId, () => ({
      feedback: previousFeedback === "like" ? null : "like",
    }));
    submitFeedback(messageId, "like", previousFeedback);
  };

  const handleDislike = (message, messageId) => {
    const previousFeedback = message?.feedback;
    handleUpdateMessage(messageId, () => ({
      feedback: previousFeedback === "dislike" ? null : "dislike",
    }));
    submitFeedback(messageId, "dislike", previousFeedback);
  };

  const shouldShowSuggestivePrompts =
    suggestedPrompts.length > 0 && hasMessages && !messageSent;

  const shouldShowCustomizationEntry =
    !isMobile &&
    showCustomizationEntry &&
    hasMessages &&
    !shouldShowSuggestivePrompts &&
    !thinking &&
    !disableChat;

  useEffect(() => {
    if (!chatBodyRef.current) return;

    const timeout = setTimeout(() => {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 150);

    return () => clearTimeout(timeout);
  }, [messages, shouldShowCustomizationEntry, displayed]);

  useEffect(() => {
    if (!activeConversation?.id || messages.length) return;
    const conversationMessages = Array.isArray(activeConversation?.messages)
      ? activeConversation.messages
      : [];
    setDisableChat(false);

    dispatch(setMessages(conversationMessages));
    setHasPromptBeenSent(conversationMessages.length > 0);

    const latestConversationMessage =
      conversationMessages[conversationMessages.length - 1];

    dispatch(
      setLiveContext({
        conversationId: activeConversation?.id || "",
        threadId: latestConversationMessage?.conversationThreadId || "",
        lastMessageId: latestConversationMessage?.id || "",
      })
    );
  }, [activeConversation]);

  useEffect(() => {
    if (activeConversation?.id) return;

    setCentrifugoChannel(null);
    messageIdsRef.current.clear();
  }, [activeConversation?.id, setCentrifugoChannel, dispatch]);

  const handleSendMessageRef = useRef(handleSendMessage);
  handleSendMessageRef.current = handleSendMessage;

  useEffect(() => {
    if (!prefilledPrompt) return;
    handleSendMessageRef.current(prefilledPrompt);
    onPromptPrefilled?.();
  }, [prefilledPrompt, onPromptPrefilled]);

  useEffect(() => {
    if (!isChatOpen) {
      setCentrifugoChannel(null);
    }
  }, [isChatOpen, setCentrifugoChannel]);

  useEffect(() => {
    const messageIds = messageIdsRef.current;
    return () => {
      messageIds.clear();
    };
  }, []);

  const lastMessage = messages[messages.length - 1];
  const clientName = callout !== "generic" ? lastMessage?.clientName : null;
  const clientCid = callout !== "generic" ? lastMessage?.clientCid : null;

  const shouldShowSafetyNet =
    (!messageSent || lastConversationFetchedId || callout === "generic") &&
    (callout === "generic" ||
      clientName ||
      clientCid ||
      messages?.length === 0 ||
      activeConversation?.messages?.length === messages?.length);
  const safetyNetContent = shouldShowSafetyNet
    ? { clientName, clientId: clientCid }
    : null;

  return (
    <Container>
      {!hasMessages && lastChatConversationId ? (
        <FlexBox
          column
          justify="center"
          align="center"
          height="100%"
          padding="1rem"
          gap="0.5rem"
        >
          <lottie-player
            src="https://assets.theinnerhour.com/compressed/ai-response-loading_1768552608412_4vzb7xt.json"
            background="transparent"
            speed="1.5"
            style={{
              width: "1.5rem",
              height: "1.5rem",
              flexShrink: 0,
            }}
            loop
            autoplay
          />
          <Body2 color={DAVYS_GREY[700]}>Loading...</Body2>
        </FlexBox>
      ) : (
        <>
          <AiChatHeader
            onHistoryClick={onHistoryClick}
            onNewChatClick={handleNewChat}
            showNewChat={hasMessages}
            onClose={onClose}
            toggleHelp={toggleHelp}
            activeTooltip={activeTooltip}
            onTooltipDismiss={() => handleTooltipDismiss("interacted")}
          />
          {hasMessages ? (
            <ChatBody ref={chatBodyRef}>
              <ChatBodyContent>
                {(isLoadingMoreMessages || hasMoreMessages) && (
                  <div
                    ref={topLoaderRef}
                    style={{
                      textAlign: "center",
                      padding: "8px",
                      fontSize: "12px",
                    }}
                  >
                    {isLoadingMoreMessages ? "Loading older messages..." : ""}
                  </div>
                )}
                <AiChatMessageList
                  messages={messages}
                  onRegenerate={handleRegenerate}
                  onLike={handleLike}
                  onDislike={handleDislike}
                  onOpenReportSheet={handleOpenReportSheet}
                  isSubmittingReport={isSubmittingReport}
                  reportSheetContext={reportSheetContext}
                  thinking={thinking}
                  disableChat={disableChat}
                  completedTyping={completedTyping}
                  setCompletedTyping={setCompletedTyping}
                  displayed={displayed}
                  setDisplayed={setDisplayed}
                  displayedLengthRef={displayedLengthRef}
                  intervalRef={intervalRef}
                />
                {shouldShowSuggestivePrompts && (
                  <AiChatSuggestivePrompts
                    onPromptSelect={handleSendMessage}
                    suggestedPrompts={suggestedPrompts}
                  />
                )}
                {shouldShowCustomizationEntry && (
                  <CustomisationCard
                    onClose={() => setShowCustomizationEntry(false)}
                    onCustomise={openCustomisationSettings}
                  />
                )}
              </ChatBodyContent>
            </ChatBody>
          ) : (
            <AiChatIntroSection
              userName={user?.name}
              greeting={greeting}
              onPromptSelect={handleSendMessage}
              suggestedPrompts={suggestedPrompts}
            />
          )}
          <AiChatInput
            onSend={handleSendMessage}
            onTypingActivity={handleTypingActivity}
            onPromptSent={handlePromptSent}
            disableChat={disableChat}
            safetyNetContent={safetyNetContent}
            textareaRef={textareaRef}
          />

          {reportSheetContext && (
            <ReportSheetOverlay>
              <ReportSheetRoot>
                <ReportReasonModal
                  onClose={handleCloseReportSheet}
                  onSubmit={handleSubmitReportSheet}
                  isSubmitting={isSubmittingReport}
                />
              </ReportSheetRoot>
            </ReportSheetOverlay>
          )}
        </>
      )}
    </Container>
  );
};

export default NewChatView;
